import { useState } from "react";
import {
  Link2,
  Globe2,
  Smartphone,
  FlaskConical,
  Clock,
  Repeat,
  Users,
  ArrowRight,
  Monitor,
} from "lucide-react";
import { GeometricBadge } from "../playful/GeometricBadge";
import { FloatingIcon } from "../playful/FloatingIcon";
import { Reveal } from "./Reveal";

const MODES = [
  {
    icon: FlaskConical,
    tone: "candy",
    title: "A/B testing",
    desc: "Split traffic by weighted percentage across variants.",
  },
  {
    icon: Globe2,
    tone: "pink",
    title: "Geo routing",
    desc: "Send each country to its own destination + fallback.",
  },
  {
    icon: Smartphone,
    tone: "yellow",
    title: "Device routing",
    desc: "iOS, Android, and desktop each get the right link.",
  },
  {
    icon: Repeat,
    tone: "mint",
    title: "Rotator",
    desc: "Round-robin across destinations on every click.",
  },
  {
    icon: Clock,
    tone: "candy",
    title: "Time windows",
    desc: "Route by day-of-week and hour, in your timezone.",
  },
  {
    icon: Users,
    tone: "pink",
    title: "Visitor rules",
    desc: "Pin a visitor to a destination with a cookie.",
  },
];

const shadow = {
  candy: "shadow-sticker-candy",
  pink: "shadow-sticker-pink",
  yellow: "shadow-sticker-yellow",
  mint: "shadow-sticker-mint",
};

// Interactive demo: geo × device → resolved destination.
const GEOS = [
  { id: "US", flag: "🇺🇸", label: "United States" },
  { id: "IN", flag: "🇮🇳", label: "India" },
  { id: "GB", flag: "🇬🇧", label: "United Kingdom" },
];
const DEVICES = [
  { id: "mobile", icon: Smartphone, label: "Mobile" },
  { id: "desktop", icon: Monitor, label: "Desktop" },
];
function resolve(geo, device) {
  if (device === "mobile") {
    if (geo === "US") return { dest: "apps.apple.com/us/yourapp", note: "US · App Store" };
    if (geo === "IN") return { dest: "play.google.com/yourapp", note: "IN · Play Store" };
    return { dest: "uk.yoursite.com/get-app", note: "UK · mobile landing" };
  }
  if (geo === "US") return { dest: "yoursite.com/welcome", note: "US · desktop site" };
  if (geo === "IN") return { dest: "in.yoursite.com", note: "IN · localized site" };
  return { dest: "uk.yoursite.com", note: "UK · localized site" };
}

function RoutingDemo() {
  const [geo, setGeo] = useState("US");
  const [device, setDevice] = useState("mobile");
  const out = resolve(geo, device);

  return (
    <div className="rounded-3xl border-2 border-ink bg-white p-6 shadow-sticker-md md:p-8">
      <div className="mb-5 flex items-center gap-2">
        <GeometricBadge tone="candy" size="sm">
          TRY IT
        </GeometricBadge>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          pick a visitor — watch the link decide
        </span>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* Controls */}
        <div className="flex flex-col gap-5">
          <div>
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
              Visitor country
            </div>
            <div className="flex flex-wrap gap-2">
              {GEOS.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setGeo(g.id)}
                  aria-pressed={geo === g.id}
                  className={`candy-press inline-flex items-center gap-2 rounded-full border-2 border-ink px-3.5 py-2 font-display text-sm font-bold shadow-sticker-xs ${geo === g.id ? "bg-candy text-white" : "bg-white text-ink"}`}
                >
                  <span>{g.flag}</span> {g.id}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
              Device
            </div>
            <div className="flex flex-wrap gap-2">
              {DEVICES.map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setDevice(id)}
                  aria-pressed={device === id}
                  className={`candy-press inline-flex items-center gap-2 rounded-full border-2 border-ink px-3.5 py-2 font-display text-sm font-bold shadow-sticker-xs ${device === id ? "bg-ink text-white" : "bg-white text-ink"}`}
                >
                  <Icon size={14} strokeWidth={2.5} /> {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Resolution */}
        <div className="flex flex-col justify-center gap-3 rounded-2xl border-2 border-ink bg-paper p-5">
          <div className="inline-flex items-center gap-2 self-start rounded-full border-2 border-ink bg-ink px-3 py-1.5 text-white shadow-sticker-xs">
            <Link2 size={13} strokeWidth={2.5} className="text-mint" />
            <span className="font-display text-sm font-bold">linklane.io/launch</span>
          </div>
          <div className="flex items-center gap-2 pl-2 text-ink/60">
            <ArrowRight size={16} strokeWidth={3} className="rotate-90" />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em]">routes to</span>
          </div>
          <div
            key={`${geo}-${device}`}
            className="pop-in rounded-2xl border-2 border-ink bg-mint-tint px-4 py-3 shadow-sticker-xs"
          >
            <div className="mb-1 font-mono text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
              {out.note}
            </div>
            <div className="break-all font-display text-base font-extrabold text-candy md:text-lg">
              {out.dest}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** RoutingSection — interactive demo + the six routing modes. */
export default function RoutingSection() {
  return (
    <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10" data-testid="routing-section">
      <Reveal className="mx-auto mb-12 max-w-2xl text-center">
        <GeometricBadge tone="yellow">SMART ROUTING</GeometricBadge>
        <h2 className="mt-5 font-display text-4xl font-extrabold leading-[0.95] tracking-tight md:text-6xl">
          One link.
          <span className="ai-gradient-text"> Six ways to route it.</span>
        </h2>
        <p className="mt-4 font-sans text-lg leading-relaxed text-muted-foreground">
          Stop maintaining a dozen links for one campaign. Point everyone at a single LinkLane URL
          and let it decide where each visitor lands.
        </p>
      </Reveal>

      {/* Interactive demo */}
      <Reveal className="mx-auto mb-12 max-w-3xl">
        <RoutingDemo />
      </Reveal>

      {/* Mode cards */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {MODES.map((m, i) => (
          <Reveal key={m.title} delay={i * 70}>
            <div
              className={`h-full border-2 border-ink ${shadow[m.tone]} sticker-hover flex items-start gap-4 rounded-3xl bg-white p-6`}
            >
              <FloatingIcon icon={m.icon} tone={m.tone} size={48} shape="square" />
              <div>
                <h3 className="font-display text-xl font-extrabold leading-tight">{m.title}</h3>
                <p className="mt-1.5 font-sans text-sm leading-relaxed text-muted-foreground">
                  {m.desc}
                </p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
